import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from '../components/LoadingButton';
import BrandMark from '../components/BrandMark';
import { Eye, EyeOff } from 'lucide-react';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    city: 'Bruxelles',
  });
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const { showSuccess, showError } = useToast();
  const { isLoading, withLoading } = useLoading();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = withLoading('register', async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      showError('Inscription', 'Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 8) {
      showError('Inscription', 'Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    try {
      await register(formData.email, formData.password, formData.name, formData.city);
      showSuccess('Compte créé', 'Votre essai d’un emprunt est disponible.');
      navigate('/library');
    } catch (error: any) {
      showError('Inscription impossible', error.message || 'Erreur lors de la création du compte');
    }
  });

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <BrandMark />
          <h1 className="font-serif text-3xl text-ink mt-8">Créer un compte</h1>
          <p className="text-sm text-ink-muted mt-2">
            Un emprunt offert, une seule fois. Déjà inscrit ?{' '}
            <Link to="/login" className="text-forest-700 hover:underline">
              Connexion
            </Link>
          </p>
        </div>
        <form onSubmit={handleSubmit} className="card p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="name">
              Nom
            </label>
            <input id="name" name="name" required value={formData.name} onChange={handleChange} className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="email">
              Email
            </label>
            <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="city">
              Ville
            </label>
            <input id="city" name="city" required value={formData.city} onChange={handleChange} className="input" />
            <p className="text-xs text-ink-faint mt-1">Ville pilote : Bruxelles. Les remises se font dans des bibliothèques de la Région.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="password">
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                className="input pr-10"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1" htmlFor="confirmPassword">
              Confirmation
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className="input"
            />
          </div>
          <p className="text-xs text-ink-muted leading-relaxed">
            En créant un compte, vous acceptez les{' '}
            <Link to="/legal/cgu" className="underline">
              conditions
            </Link>{' '}
            et la{' '}
            <Link to="/legal/garantie" className="underline">
              charte de garantie
            </Link>
            .
          </p>
          <LoadingButton type="submit" loading={isLoading('register')} loadingText="Création…" className="w-full" size="lg">
            Créer le compte
          </LoadingButton>
        </form>
      </div>
    </div>
  );
};

export default Register;
